import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ConditionalRendering } from '@common-components/conditional-rendering';
import Tooltip from '@common-components/tooltip/Tooltip';
import { useAppDispatch, useExchangeConditions } from '@hooks/index';
import { currencyTools } from '@utils/tools/currencyTools';
import useEnrollmentHistory from '../hooks/useEnrollmentHistory';
import { ENROLLMENT_PLAN_STATUS, IEnrollmentDetails } from './enrollmentHistoryTypes';

const StyledContainer = styled.div`
    .grid-body{
        .title{
            flex: 2 1 0;
        }
    }
`;

const PlanSummaryDetails = (props: { enrollmentDetails: IEnrollmentDetails }) => {
    const { enrollmentDetails } = { ...props };
    const dispatch = useAppDispatch();
    const { isStateSubsidyVisible, isPrimiumCreditVisible, isPendStatusVisible, isStateSubsidy2Visible } = useExchangeConditions();
    const { sumSubsidyAndPremiumCredit } = useEnrollmentHistory(enrollmentDetails, dispatch);
    const hasStateSubsidyWithoutPrimium = enrollmentDetails.electedStateSubsidy !== undefined && enrollmentDetails.electedStateSubsidy !== null && enrollmentDetails.electedStateSubsidy !== 'null' && (enrollmentDetails.statePremiumCreditAmt === 'null' || enrollmentDetails.statePremiumCreditAmt === null);
    const hasPremiumWithSubsidy = enrollmentDetails.electedStateSubsidy !== undefined && enrollmentDetails.electedStateSubsidy !== null && enrollmentDetails.electedStateSubsidy !== 'null' && enrollmentDetails.statePremiumCreditAmt !== 'null' && enrollmentDetails.statePremiumCreditAmt !== null;
    const hasPremiumWithoutSubsidy = (enrollmentDetails.electedStateSubsidy === undefined || enrollmentDetails.electedStateSubsidy === null || enrollmentDetails.electedStateSubsidy === 'null') && enrollmentDetails.statePremiumCreditAmt != null && enrollmentDetails.statePremiumCreditAmt !== 'null';
    const hasElectedAPTC=enrollmentDetails.electedAptc!==null && enrollmentDetails.electedAptc!=='null';
    const hasStateSubsidy2 = !!(enrollmentDetails.appliedStateSubsidy2 && enrollmentDetails.appliedStateSubsidy2 !== 'null');

    const createLabel = (key) => `indportal.enrollments.label.${key}`;
    const getEnrollmentStatus = (status: string) => {
        const STATUS_LABELS = {
            [ENROLLMENT_PLAN_STATUS.PENDING.value]: createLabel('pending'),
            [ENROLLMENT_PLAN_STATUS.CANCELLED.value]: createLabel('cancelled'),
            [ENROLLMENT_PLAN_STATUS.CANCELLED.value]: createLabel('cancelled'),
            [ENROLLMENT_PLAN_STATUS.TERMINATED.value]: createLabel('terminated'),
            [ENROLLMENT_PLAN_STATUS.PEND_CANCELED.value]: createLabel('terminated'),
            [ENROLLMENT_PLAN_STATUS.ENROLLED.value]: createLabel('enrolled'),
            [ENROLLMENT_PLAN_STATUS.ABORTED.value]: createLabel('aborted'),
        };

        if (status.toUpperCase() === ENROLLMENT_PLAN_STATUS.PEND.value) {
            return isPendStatusVisible ? createLabel('pend') : '';
        }
        return STATUS_LABELS[status.toUpperCase()] || '';
    };

    const getPendingStatusTooltip = (status) => {
        const statusKey = status.toUpperCase();
        let tooltip;
        // Determine the tooltip based on the status
        if (statusKey === ENROLLMENT_PLAN_STATUS.PEND.value && isPendStatusVisible) {
            tooltip = 'indportal.plansummary.enrollmentStatusPendTooltip';
        } else if (statusKey === ENROLLMENT_PLAN_STATUS.PENDING.value) {
            tooltip = 'indportal.plansummary.enrollmentCompletePendingTooltip';
        }
        return tooltip ? (
            <Tooltip
                id={getEnrollmentStatus(status)}
                index="1"
                content={tooltip}
                place="top"
                triggerDecorationClassName="no-border"
            />
        ) : (
            <FormattedMessage id={getEnrollmentStatus(status)} />
        );
    };

    const getPlanDetails = () => {
        return (
            <>
                <div>
                    <span className='title'><FormattedMessage id="plansummary.coverageStartDate" />:</span>
                    <span>{enrollmentDetails.coverageStartDate}</span>
                </div>
                <div>
                    <span className='title'><FormattedMessage id="plansummary.coverageEndDate" />:</span>
                    <span>{enrollmentDetails.coverageEndDate}</span>
                </div>
                <div>
                    <span className='title'><FormattedMessage id="indportal.enrollments.enrollmentStatus" /></span>
                    <span className='tooltip-container no-link' data-testid='status-tooltip'>{getPendingStatusTooltip(enrollmentDetails.enrollmentStatus)}</span>
                </div>
                <div>
                    <span className='title'><FormattedMessage id="indportal.enrollments.monthlyPremium" /></span>
                    <span>{currencyTools.getMoneyFormat(parseFloat(enrollmentDetails.monthlypremium), false)}</span>
                </div>
                <ConditionalRendering isTrue={hasElectedAPTC}>
                    <div>
                        <span className='title'><FormattedMessage id="indportal.enrollments.electedAPTC" /></span>
                        <span>{currencyTools.getMoneyFormat(parseFloat(enrollmentDetails.electedAptc), false)}</span>
                    </div>
                </ConditionalRendering>
                <ConditionalRendering isTrue={isStateSubsidyVisible && hasStateSubsidyWithoutPrimium}>
                    <div>
                        <span className='title'><FormattedMessage id="indportal.enrollments.electedStateSubsidy" /></span>
                        <span>{currencyTools.getMoneyFormat(parseFloat(enrollmentDetails.electedStateSubsidy), false)}</span>
                    </div>
                </ConditionalRendering>
                <ConditionalRendering isTrue={isStateSubsidy2Visible && hasStateSubsidy2}>
                    <div>
                        <span className='title'><FormattedMessage id="indportal.enrollments.appliedStateSubsidy2" /></span>
                        <span>{currencyTools.getMoneyFormat(parseFloat(enrollmentDetails.appliedStateSubsidy2!), false)}</span>
                    </div>
                </ConditionalRendering>
                <ConditionalRendering isTrue={isPrimiumCreditVisible && hasPremiumWithSubsidy}>
                    <div>
                        <span className='title'><FormattedMessage id="indportal.enrollments.premiumCredit" /></span>
                        <span>{currencyTools.getMoneyFormat(sumSubsidyAndPremiumCredit(enrollmentDetails.electedStateSubsidy, enrollmentDetails.statePremiumCreditAmt), false)}</span>
                    </div>
                </ConditionalRendering>
                <ConditionalRendering isTrue={isPrimiumCreditVisible && hasPremiumWithoutSubsidy}>
                    <div>
                        <span className='title'><FormattedMessage id="indportal.enrollments.premiumCredit" /></span>
                        <span>{currencyTools.getMoneyFormat(parseFloat(enrollmentDetails.statePremiumCreditAmt), false)}</span>
                    </div>
                </ConditionalRendering>
                <div>
                    <span className='title'><FormattedMessage id="indportal.enrollments.netPremium" /></span>
                    <span>{currencyTools.getMoneyFormat(parseFloat(enrollmentDetails.netPremium), false)}</span>
                </div>
                <div>
                    <span className='tooltip-container title'><FormattedMessage id="plansummary.premiumEffectiveDate" />
                        <Tooltip id={<FontAwesomeIcon className='text-orange premium-date-tooltip padding-left-05' icon={faQuestionCircle} />} triggerDecorationClassName='no-border' index='1' content="plansummary.premiumEffectiveDateTooltip" place='top'></Tooltip> :
                    </span>
                    <span>{enrollmentDetails.premiumEffDate}</span>
                </div>
            </>
        );
    };

    return (
        <StyledContainer className="col-xs-12 col-md-6 col-lg-6" data-testid="planSummary" id="planSummary">
            <div className='gi-table gi-usa-table' data-testid="planSummaryHeader">
                <div className='header-border'>
                    <span className='grid-header'>
                        <FormattedMessage id="indportal.plansummary.title" />
                    </span>
                </div>
                <div className='grid-body'>
                    {getPlanDetails()}
                </div>
            </div>
        </StyledContainer>
    );
};


export default PlanSummaryDetails;